import * as React from 'react';
import { ArrowBack, Edit } from '@mui/icons-material';
import { Button } from 'components';
import { useNavigate } from 'react-router';
import { useLocalize } from 'store/contexts/LocalizationContext/LocalizationContext.provider';
import { OrderStatusProgress } from '../OrderStatusProgress/OrderStatusProgress';
import { formatOrderTimestamp, getDeliveryLabelKey } from '../../Orders.utils';
import {
  formatOrderPaymentJson,
  getOrderLineItemProductHref,
  navigateToOrderLineProduct,
} from './OrderDetail.utils';
import { TOrderDetail } from './OrderDetail.types';
import './OrderDetail.styled.scss';

export const OrderDetail: TOrderDetail = ({ order, onBack, onEdit }) => {
  const navigate = useNavigate();
  const localize = useLocalize();
  const deliveryKey = getDeliveryLabelKey(order.shippingData.deliveryMethod);
  const deliveryLabel = deliveryKey
    ? (localize(deliveryKey) as string)
    : order.shippingData.deliveryMethod ||
      (localize('admin.orders.delivery.unknown') as string);
  const paymentText = formatOrderPaymentJson(order.payment);
  const yesLabel = localize('admin.orders.detail.yes') as string;
  const noLabel = localize('admin.orders.detail.no') as string;

  return (
    <div className="admin-orders-detail">
      <div className="admin-orders-detail__toolbar">
        <Button
          type="button"
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={onBack}
        >
          {localize('admin.orders.back')}
        </Button>
        <div className="admin-orders-detail__toolbar-center">
          <h2 className="h5 admin-orders-detail__title">{order.orderNumber}</h2>
        </div>
        <Button
          type="button"
          variant="contained"
          startIcon={<Edit />}
          onClick={onEdit}
        >
          {localize('admin.orders.edit')}
        </Button>
      </div>

      <div className="admin-orders-detail__body">
        <section className="admin-orders-detail__section">
          <h3 className="h6 admin-orders-detail__section-title">
            {localize('admin.orders.detail.section.identifiers')}
          </h3>
          <div className="admin-orders-detail__grid">
            <div className="admin-orders-detail__field">
              <span className="caption admin-orders-detail__label">
                {localize('admin.orders.detail.label.internalid')}
              </span>
              <span className="body2 admin-orders-detail__value admin-orders-detail__value--mono">
                {order.id}
              </span>
            </div>
            <div className="admin-orders-detail__field">
              <span className="caption admin-orders-detail__label">
                {localize('admin.orders.detail.label.ordernumber')}
              </span>
              <p className="body1 admin-orders-detail__value">
                {order.orderNumber}
              </p>
            </div>
            <div className="admin-orders-detail__field">
              <span className="caption admin-orders-detail__label">
                {localize('admin.orders.detail.label.reservation')}
              </span>
              <span className="body2 admin-orders-detail__value admin-orders-detail__value--mono">
                {order.reservationId}
              </span>
            </div>
            <div className="admin-orders-detail__field">
              <span className="caption admin-orders-detail__label">
                {localize('admin.orders.userid')}
              </span>
              <span className="body2 admin-orders-detail__value admin-orders-detail__value--mono">
                {order.userId ??
                  (localize('admin.orders.customer.missing') as string)}
              </span>
            </div>
            <div className="admin-orders-detail__field">
              <span className="caption admin-orders-detail__label">
                {localize('admin.orders.detail.label.status')}
              </span>
              <p className="body1 admin-orders-detail__value">{order.status}</p>
            </div>
          </div>
          <div className="admin-orders-detail__progress-wrap">
            <OrderStatusProgress status={order.status} />
          </div>
        </section>

        <section className="admin-orders-detail__section">
          <h3 className="h6 admin-orders-detail__section-title">
            {localize('admin.orders.detail.section.timestamps')}
          </h3>
          <div className="admin-orders-detail__grid">
            <div className="admin-orders-detail__field">
              <span className="caption admin-orders-detail__label">
                {localize('admin.orders.created')}
              </span>
              <p className="body1 admin-orders-detail__value">
                {formatOrderTimestamp(order.createdAt)}
              </p>
            </div>
            <div className="admin-orders-detail__field">
              <span className="caption admin-orders-detail__label">
                {localize('admin.orders.updated')}
              </span>
              <p className="body1 admin-orders-detail__value">
                {formatOrderTimestamp(order.updatedAt)}
              </p>
            </div>
          </div>
        </section>

        <section className="admin-orders-detail__section">
          <h3 className="h6 admin-orders-detail__section-title">
            {localize('admin.orders.detail.section.financial')}
          </h3>
          <div className="admin-orders-detail__grid">
            <div className="admin-orders-detail__field">
              <span className="caption admin-orders-detail__label">
                {localize('admin.orders.total')}
              </span>
              <p className="body1 admin-orders-detail__value">
                {order.totalPrice.toFixed(2)}
              </p>
            </div>
            <div className="admin-orders-detail__field">
              <span className="caption admin-orders-detail__label">
                {localize('admin.orders.detail.label.payed')}
              </span>
              <p className="body1 admin-orders-detail__value">
                {order.payed ? yesLabel : noLabel}
              </p>
            </div>
            <div className="admin-orders-detail__field">
              <span className="caption admin-orders-detail__label">
                {localize('admin.orders.detail.label.payment')}
              </span>
              {paymentText ? (
                <pre className="admin-orders-detail__json">{paymentText}</pre>
              ) : (
                <p className="body1 admin-orders-detail__value">
                  {localize('admin.orders.detail.payment.empty')}
                </p>
              )}
            </div>
          </div>
        </section>

        <section className="admin-orders-detail__section">
          <h3 className="h6 admin-orders-detail__section-title">
            {localize('admin.orders.detail.section.flags')}
          </h3>
          <div className="admin-orders-detail__grid">
            <div className="admin-orders-detail__field">
              <span className="caption admin-orders-detail__label">
                {localize('admin.orders.detail.label.receive.notifications')}
              </span>
              <p className="body1 admin-orders-detail__value">
                {order.receiveNotifications ? yesLabel : noLabel}
              </p>
            </div>
            <div className="admin-orders-detail__field">
              <span className="caption admin-orders-detail__label">
                {localize('admin.orders.detail.label.policy')}
              </span>
              <p className="body1 admin-orders-detail__value">
                {order.policyAccepted ? yesLabel : noLabel}
              </p>
            </div>
          </div>
        </section>

        <section className="admin-orders-detail__section">
          <h3 className="h6 admin-orders-detail__section-title">
            {localize('admin.orders.detail.section.shipping')}
          </h3>
          <div className="admin-orders-detail__grid">
            <div className="admin-orders-detail__field">
              <span className="caption admin-orders-detail__label">
                {localize('admin.orders.detail.shipping.firstname')}
              </span>
              <p className="body1 admin-orders-detail__value">
                {order.shippingData.firstName}
              </p>
            </div>
            <div className="admin-orders-detail__field">
              <span className="caption admin-orders-detail__label">
                {localize('admin.orders.detail.shipping.lastname')}
              </span>
              <p className="body1 admin-orders-detail__value">
                {order.shippingData.lastName}
              </p>
            </div>
            <div className="admin-orders-detail__field">
              <span className="caption admin-orders-detail__label">
                {localize('admin.orders.detail.shipping.email')}
              </span>
              <p className="body1 admin-orders-detail__value">
                {order.shippingData.email}
              </p>
            </div>
            <div className="admin-orders-detail__field">
              <span className="caption admin-orders-detail__label">
                {localize('admin.orders.detail.shipping.phone')}
              </span>
              <p className="body1 admin-orders-detail__value">
                {order.shippingData.phone}
              </p>
            </div>
            <div className="admin-orders-detail__field">
              <span className="caption admin-orders-detail__label">
                {localize('admin.orders.detail.shipping.street')}
              </span>
              <p className="body1 admin-orders-detail__value">
                {order.shippingData.street}
              </p>
            </div>
            <div className="admin-orders-detail__field">
              <span className="caption admin-orders-detail__label">
                {localize('admin.orders.detail.shipping.building')}
              </span>
              <p className="body1 admin-orders-detail__value">
                {order.shippingData.buildingNumber}
              </p>
            </div>
            <div className="admin-orders-detail__field">
              <span className="caption admin-orders-detail__label">
                {localize('admin.orders.detail.shipping.apartment')}
              </span>
              <p className="body1 admin-orders-detail__value">
                {order.shippingData.apartamentNumber}
              </p>
            </div>
            <div className="admin-orders-detail__field">
              <span className="caption admin-orders-detail__label">
                {localize('admin.orders.detail.shipping.city')}
              </span>
              <p className="body1 admin-orders-detail__value">
                {order.shippingData.city}
              </p>
            </div>
            <div className="admin-orders-detail__field">
              <span className="caption admin-orders-detail__label">
                {localize('admin.orders.detail.shipping.postal')}
              </span>
              <p className="body1 admin-orders-detail__value">
                {order.shippingData.postalCode}
              </p>
            </div>
            <div className="admin-orders-detail__field">
              <span className="caption admin-orders-detail__label">
                {localize('admin.orders.detail.shipping.delivery')}
              </span>
              <p className="body1 admin-orders-detail__value">
                {deliveryLabel}
              </p>
            </div>
            <div className="admin-orders-detail__field">
              <span className="caption admin-orders-detail__label">
                {localize('admin.orders.detail.shipping.parcel')}
              </span>
              <p className="body1 admin-orders-detail__value">
                {order.shippingData.parcelLockerCode ||
                  (localize('admin.orders.customer.missing') as string)}
              </p>
            </div>
            <div className="admin-orders-detail__field">
              <span className="caption admin-orders-detail__label">
                {localize('admin.orders.detail.shipping.additional')}
              </span>
              <p className="body1 admin-orders-detail__value">
                {order.shippingData.additionalInfo ??
                  (localize('admin.orders.customer.missing') as string)}
              </p>
            </div>
          </div>
        </section>

        <section className="admin-orders-detail__section">
          <h3 className="h6 admin-orders-detail__section-title">
            {localize('admin.orders.detail.section.items')}
          </h3>
          <div className="admin-orders-detail__line-items">
            {order.checkoutItems.map((line, index) => {
              const productHref = getOrderLineItemProductHref(
                line.productType,
                line.productId
              );
              const lineHeadInner = (
                <>
                  <img
                    className="admin-orders-detail__line-thumb"
                    src={line.productImage}
                    alt=""
                  />
                  <div className="admin-orders-detail__line-meta">
                    <p className="body1 font-semibold admin-orders-detail__line-product">
                      {localize(line.productName)}
                    </p>
                    <p className="body2 admin-orders-detail__line-product-id">
                      {localize('admin.orders.detail.label.productid')}:{' '}
                      <span className="admin-orders-detail__value--mono">
                        {line.productId}
                      </span>
                    </p>
                    <p className="body2 admin-orders-detail__line-product-type">
                      {localize('admin.orders.detail.label.producttype')}:{' '}
                      {line.productType}
                    </p>
                    <div className="admin-orders-detail__grid">
                      <div className="admin-orders-detail__field">
                        <span className="caption admin-orders-detail__label">
                          {localize('admin.orders.detail.label.unitprice')}
                        </span>
                        <p className="body1 admin-orders-detail__value">
                          {line.price.toFixed(2)}
                        </p>
                      </div>
                      <div className="admin-orders-detail__field">
                        <span className="caption admin-orders-detail__label">
                          {localize('admin.orders.detail.label.quantity')}
                        </span>
                        <p className="body1 admin-orders-detail__value">
                          {line.quantity}
                        </p>
                      </div>
                      <div className="admin-orders-detail__field">
                        <span className="caption admin-orders-detail__label">
                          {localize('admin.orders.detail.label.linetotal')}
                        </span>
                        <p className="body1 admin-orders-detail__value">
                          {(line.price * line.quantity).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              );

              return (
                <div
                  key={`${order.id}-line-${index}`}
                  className="admin-orders-detail__line-item"
                >
                  {productHref ? (
                    <div
                      className="admin-orders-detail__line-item-head-link"
                      role="button"
                      tabIndex={0}
                      aria-label={
                        localize(
                          'admin.orders.detail.product.navigate'
                        ) as string
                      }
                      onClick={() =>
                        navigateToOrderLineProduct(
                          navigate,
                          line.productType,
                          line.productId
                        )
                      }
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          navigateToOrderLineProduct(
                            navigate,
                            line.productType,
                            line.productId
                          );
                        }
                      }}
                    >
                      <div className="admin-orders-detail__line-item-head">
                        {lineHeadInner}
                      </div>
                    </div>
                  ) : (
                    <div className="admin-orders-detail__line-item-head">
                      {lineHeadInner}
                    </div>
                  )}
                  <div>
                    <span className="caption admin-orders-detail__label">
                      {localize('admin.orders.detail.label.stock')}
                    </span>
                    <ul className="admin-orders-detail__stock-list body2">
                      {line.stock.map((s, si) => (
                        <li key={`${order.id}-line-${index}-stock-${si}`}>
                          {s.name} — {s.variation}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
};
